import { useState, useEffect, useMemo } from 'react';
import { parseTradeDeadline, tradeDeadlineLabel, type TradeDeadline } from '@/lib/nflTradeDeadline';
import { NFLTradeProposeMobile } from './NFLTradeProposeMobile';
import { useIsMobile } from '@/hooks/use-mobile';
import { supabase } from '@/integrations/supabase/client';
import { useNFLLeague } from '@/contexts/NFLLeagueContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  ArrowLeftRight,
  Plus,
  X,
  Check,
  Clock,
  Send,
  Loader2,
  ArrowRight,
  Ban,
  RefreshCw,
  Shield,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { formatDistanceToNow } from 'date-fns';

const positionColors: Record<string, string> = {
  QB: 'bg-red-500/20 text-red-400 border-red-500/30',
  RB: 'bg-green-500/20 text-green-400 border-green-500/30',
  WR: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  TE: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  K: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  DST: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
};

interface RosterPlayer {
  player_id: string;
  full_name: string;
  position: string;
  position_slot: string;
}

interface TradeData {
  id: string;
  league_id: string;
  proposer_team_id: string;
  receiver_team_id: string;
  proposer_players: string[];
  receiver_players: string[];
  proposer_picks: any[];
  receiver_picks: any[];
  message: string | null;
  status: string;
  expires_at: string | null;
  created_at: string;
  proposer_team_name?: string;
  receiver_team_name?: string;
  proposer_player_details?: { id: string; full_name: string; position: string }[];
  receiver_player_details?: { id: string; full_name: string; position: string }[];
}

interface TeamOption {
  id: string;
  name: string;
  owner_id: string;
}

interface NFLTradePageProps {
  /** SAB-1046: team to pre-select as the trade partner (from a standings row). */
  initialTeamId?: string | null;
  /** SAB-1062: player to pre-add to THEIR side (from a player-detail Trade tap). */
  initialPlayerId?: string | null;
}

export default function NFLTradePage({ initialTeamId, initialPlayerId }: NFLTradePageProps = {}) {
  const { currentLeague, currentTeam, teams } = useNFLLeague();
  const { user } = useAuth();
  const { toast } = useToast();

  const [trades, setTrades] = useState<TradeData[]>([]);
  const [loading, setLoading] = useState(true);
  // SAB-1046: a pre-targeted partner arrives from a standings row, so the propose DIALOG
  // opens with it. Seeding only `selectedTeamId` left the user on the Inbox tab with an
  // invisible selection -- the pre-target was real and had no effect anyone could see,
  // which is indistinguishable from it not working. Verified by driving it, not by reading
  // the state.
  const [proposeOpen, setProposeOpen] = useState(!!initialTeamId);
  const isMobile = useIsMobile();

  // SAB-1058: the DERIVED deadline, read from the same function nfl-trade refuses on.
  // The old header read `currentLeague.trade_deadline` -- a stored timestamp that no NFL
  // league had, so the line never rendered while trading stayed open through the playoffs.
  const [deadline, setDeadline] = useState<TradeDeadline | null>(null);
  useEffect(() => {
    if (!currentLeague?.id) return;
    let cancelled = false;
    (async () => {
      const { data } = await (supabase as any).rpc('nfl_trade_deadline', { p_league_id: currentLeague.id });
      if (!cancelled) setDeadline(parseTradeDeadline(data));
    })();
    return () => { cancelled = true; };
  }, [currentLeague?.id]);

  // Proposal state
  // SAB-1046: pre-targeted from a standings row. Seeded as the INITIAL value rather than
  // set in an effect, so the roster load below fires once on mount instead of racing an
  // empty first render.
  const [selectedTeamId, setSelectedTeamId] = useState(initialTeamId ?? '');
  const [myRoster, setMyRoster] = useState<RosterPlayer[]>([]);
  const [theirRoster, setTheirRoster] = useState<RosterPlayer[]>([]);
  const [mySelected, setMySelected] = useState<string[]>([]);
  // SAB-1062: seeded, not set in an effect. Their roster load already keys off
  // selectedTeamId; setting this afterwards would race that fetch and clear itself.
  const [theirSelected, setTheirSelected] = useState<string[]>(initialPlayerId ? [initialPlayerId] : []);
  const [tradeMessage, setTradeMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const otherTeams = useMemo(
    () => (teams || []).filter(t => t.id !== currentTeam?.id),
    [teams, currentTeam]
  );

  // Load trades
  useEffect(() => {
    if (!currentLeague || !currentTeam) return;
    loadTrades();
  }, [currentLeague, currentTeam]);

  async function loadTrades() {
    if (!currentLeague || !currentTeam) return;
    setLoading(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const res = await supabase.functions.invoke('nfl-trade', {
        body: { action: 'get_trades', league_id: currentLeague.id, team_id: currentTeam.id },
        headers: { Authorization: `Bearer ${session.session?.access_token}` },
      });
      if (res.data?.trades) setTrades(res.data.trades);
    } catch (err) {
      console.error('Failed to load trades:', err);
    } finally {
      setLoading(false);
    }
  }

  // Load rosters when proposing
  useEffect(() => {
    if (!proposeOpen || !currentLeague || !currentTeam) return;
    loadMyRoster();
  }, [proposeOpen, currentLeague, currentTeam]);

  useEffect(() => {
    if (!selectedTeamId || !currentLeague) return;
    loadTheirRoster(selectedTeamId);
  }, [selectedTeamId, currentLeague]);

  async function loadMyRoster() {
    if (!currentTeam || !currentLeague) return;
    const { data } = await supabase
      .from('nfl_rosters' as any)
      .select('player_id, position_slot, nfl_players!inner(full_name, position)')
      .eq('team_id', currentTeam.id)
      .eq('league_id', currentLeague.id)
      .eq('is_active', true);
    setMyRoster(
      (data || []).map((r: any) => ({
        player_id: r.player_id,
        full_name: r.nfl_players?.full_name || 'Unknown',
        position: r.nfl_players?.position || '?',
        position_slot: r.position_slot,
      }))
    );
  }

  async function loadTheirRoster(teamId: string) {
    if (!currentLeague) return;
    const { data } = await supabase
      .from('nfl_rosters' as any)
      .select('player_id, position_slot, nfl_players!inner(full_name, position)')
      .eq('team_id', teamId)
      .eq('league_id', currentLeague.id)
      .eq('is_active', true);
    setTheirRoster(
      (data || []).map((r: any) => ({
        player_id: r.player_id,
        full_name: r.nfl_players?.full_name || 'Unknown',
        position: r.nfl_players?.position || '?',
        position_slot: r.position_slot,
      }))
    );
  }

  function resetProposal() {
    setSelectedTeamId('');
    setMySelected([]);
    setTheirSelected([]);
    setTheirRoster([]);
    setTradeMessage('');
  }

  async function handlePropose() {
    if (!currentTeam || mySelected.length === 0 || theirSelected.length === 0) return;
    setSubmitting(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const res = await supabase.functions.invoke('nfl-trade', {
        body: {
          action: 'propose',
          team_id: currentTeam.id,
          receiver_team_id: selectedTeamId,
          proposer_players: mySelected,
          receiver_players: theirSelected,
          message: tradeMessage || undefined,
        },
        headers: { Authorization: `Bearer ${session.session?.access_token}` },
      });
      if (res.data?.error) {
        toast({ title: 'Error', description: res.data.error, variant: 'destructive' });
      } else {
        toast({ title: 'Trade Proposed', description: 'Your trade offer has been sent.' });
        setProposeOpen(false);
        resetProposal();
        loadTrades();
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to propose trade', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  }

  async function handleTradeAction(tradeId: string, action: string) {
    try {
      const { data: session } = await supabase.auth.getSession();
      const res = await supabase.functions.invoke('nfl-trade', {
        body: { action, trade_id: tradeId },
        headers: { Authorization: `Bearer ${session.session?.access_token}` },
      });
      if (res.data?.error) {
        toast({ title: 'Error', description: res.data.error, variant: 'destructive' });
      } else {
        const labels: Record<string, string> = {
          accept: 'Trade accepted, under review for 24h',
          decline: 'Trade declined',
          cancel: 'Trade cancelled',
          veto: 'Trade vetoed',
        };
        toast({ title: 'Success', description: labels[action] || 'Done' });
        loadTrades();
      }
    } catch (err) {
      toast({ title: 'Error', description: `Failed to ${action} trade`, variant: 'destructive' });
    }
  }

  const incoming = trades.filter(t => t.receiver_team_id === currentTeam?.id && t.status === 'pending');
  const outgoing = trades.filter(t => t.proposer_team_id === currentTeam?.id && t.status === 'pending');
  const underReview = trades.filter(t => t.status === 'under_review');
  const history = trades.filter(t => !['pending', 'under_review'].includes(t.status));

  const isCommissioner = currentLeague?.commissioner_id === user?.id;

  if (!currentLeague || !currentTeam) {
    return <p className="text-center text-muted-foreground py-12">Join a league to access trades.</p>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold">Trades</h1>
          {deadline && (
            <p className={cn('text-xs mt-1', deadline.has_passed ? 'text-amber-400' : 'text-muted-foreground')}>
              {tradeDeadlineLabel(deadline)}
            </p>
          )}
        </div>
        {/* SAB-1058: disabled past the deadline, and the WHY is on the control rather
            than only in a toast after the server refuses. The server is still the
            authority -- this saves a round trip, it does not replace the refusal. */}
        <Button
          onClick={() => setProposeOpen(true)}
          disabled={!!deadline?.has_passed}
          title={deadline?.has_passed ? tradeDeadlineLabel(deadline) : undefined}
        >
          <ArrowLeftRight className="w-4 h-4 mr-2" />
          Propose Trade
        </Button>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="incoming">
        <TabsList className="w-full">
          <TabsTrigger value="incoming" className="flex-1">
            Inbox
            {incoming.length > 0 && (
              <Badge variant="destructive" className="ml-2 h-5 min-w-[20px] p-0 flex items-center justify-center text-xs">
                {incoming.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="outgoing" className="flex-1">
            Sent
            {outgoing.length > 0 && (
              <Badge variant="secondary" className="ml-2 h-5 min-w-[20px] p-0 flex items-center justify-center text-xs">
                {outgoing.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="review" className="flex-1">
            Review
            {underReview.length > 0 && (
              <Badge className="ml-2 h-5 min-w-[20px] p-0 flex items-center justify-center text-xs bg-amber-500">
                {underReview.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="history" className="flex-1">History</TabsTrigger>
        </TabsList>

        <TabsContent value="incoming" className="space-y-4 mt-4">
          {loading ? (
            <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></div>
          ) : incoming.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No pending trade offers</p>
          ) : (
            incoming.map(trade => (
              <TradeCard
                key={trade.id}
                trade={trade}
                perspective="incoming"
                isCommissioner={isCommissioner}
                onAction={handleTradeAction}
              />
            ))
          )}
        </TabsContent>

        <TabsContent value="outgoing" className="space-y-4 mt-4">
          {loading ? (
            <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></div>
          ) : outgoing.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No sent trade offers</p>
          ) : (
            outgoing.map(trade => (
              <TradeCard
                key={trade.id}
                trade={trade}
                perspective="outgoing"
                isCommissioner={isCommissioner}
                onAction={handleTradeAction}
              />
            ))
          )}
        </TabsContent>

        <TabsContent value="review" className="space-y-4 mt-4">
          {underReview.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No trades under review</p>
          ) : (
            underReview.map(trade => (
              <TradeCard
                key={trade.id}
                trade={trade}
                perspective="review"
                isCommissioner={isCommissioner}
                onAction={handleTradeAction}
              />
            ))
          )}
        </TabsContent>

        <TabsContent value="history" className="space-y-4 mt-4">
          {history.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No trade history yet</p>
          ) : (
            history.slice(0, 20).map(trade => (
              <TradeCard
                key={trade.id}
                trade={trade}
                perspective="history"
                isCommissioner={false}
                onAction={handleTradeAction}
              />
            ))
          )}
        </TabsContent>
      </Tabs>

      {/* SAB-1054: on a phone the desktop dialog is replaced outright, not restyled.
          Rendering both and hiding one with CSS would keep two focus traps and two copies
          of every input in the tree. */}
      {isMobile ? (
        <NFLTradeProposeMobile
          open={proposeOpen}
          onOpenChange={(o) => { if (!o) resetProposal(); setProposeOpen(o); }}
          myTeamName={currentTeam?.name ?? 'You'}
          otherTeams={otherTeams}
          selectedTeamId={selectedTeamId}
          onSelectTeam={(v) => { setSelectedTeamId(v); setTheirSelected([]); }}
          myRoster={myRoster}
          theirRoster={theirRoster}
          mySelected={mySelected}
          theirSelected={theirSelected}
          setMySelected={setMySelected}
          setTheirSelected={setTheirSelected}
          message={tradeMessage}
          setMessage={setTradeMessage}
          submitting={submitting}
          // The SAME handler the desktop modal calls. A second submit path is how two
          // surfaces come to disagree about what a proposal is.
          onSubmit={handlePropose}
          positionColors={positionColors}
        />
      ) : null}

      {/* Propose Trade Dialog */}
      <Dialog open={proposeOpen && !isMobile} onOpenChange={(o) => { if (!o) resetProposal(); setProposeOpen(o); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowLeftRight className="w-5 h-5" />
              Propose Trade
            </DialogTitle>
            <DialogDescription>
              Select players from each side to build your trade offer
            </DialogDescription>
          </DialogHeader>

          {/* Team selector */}
          <div>
            <label className="text-sm text-muted-foreground mb-1.5 block">Trade with</label>
            <Select value={selectedTeamId} onValueChange={(v) => { setSelectedTeamId(v); setTheirSelected([]); }}>
              <SelectTrigger>
                <SelectValue placeholder="Select a team..." />
              </SelectTrigger>
              <SelectContent>
                {otherTeams.map(team => (
                  <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedTeamId && (
            <>
              <div className="grid grid-cols-2 gap-4 flex-1 min-h-0 overflow-auto">
                {/* My side */}
                <div className="flex flex-col border rounded-lg overflow-hidden">
                  <div className="p-3 bg-primary/10 border-b">
                    <h3 className="font-semibold text-sm">{currentTeam?.name} gives</h3>
                  </div>
                  {/* Selected */}
                  <div className="p-2 border-b min-h-[60px]">
                    {mySelected.length === 0 ? (
                      <p className="text-xs text-muted-foreground text-center py-3">Tap players below to add</p>
                    ) : (
                      <div className="space-y-1">
                        {mySelected.map(pid => {
                          const p = myRoster.find(r => r.player_id === pid);
                          return (
                            <div key={pid} className="flex items-center gap-2 p-1.5 bg-muted/50 rounded text-sm">
                              <Badge variant="outline" className={cn("text-xs", positionColors[p?.position || ''])}>
                                {p?.position}
                              </Badge>
                              <span className="flex-1 truncate font-medium">{p?.full_name}</span>
                              <button onClick={() => setMySelected(s => s.filter(id => id !== pid))}>
                                <X className="w-3 h-3 text-muted-foreground" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {/* Available */}
                  <div className="flex-1 overflow-auto p-2 space-y-0.5 max-h-[200px]">
                    {myRoster.filter(r => !mySelected.includes(r.player_id)).map(player => (
                      <button
                        key={player.player_id}
                        onClick={() => setMySelected(s => [...s, player.player_id])}
                        className="w-full flex items-center gap-2 p-1.5 rounded hover:bg-muted/50 transition-colors text-left text-sm"
                      >
                        <Plus className="w-3 h-3 text-muted-foreground" />
                        <Badge variant="outline" className={cn("text-xs", positionColors[player.position])}>
                          {player.position}
                        </Badge>
                        <span className="flex-1 truncate">{player.full_name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Their side */}
                <div className="flex flex-col border rounded-lg overflow-hidden">
                  <div className="p-3 bg-destructive/10 border-b">
                    <h3 className="font-semibold text-sm">
                      {otherTeams.find(t => t.id === selectedTeamId)?.name} gives
                    </h3>
                  </div>
                  {/* Selected */}
                  <div className="p-2 border-b min-h-[60px]">
                    {theirSelected.length === 0 ? (
                      <p className="text-xs text-muted-foreground text-center py-3">Tap players below to add</p>
                    ) : (
                      <div className="space-y-1">
                        {theirSelected.map(pid => {
                          const p = theirRoster.find(r => r.player_id === pid);
                          return (
                            <div key={pid} className="flex items-center gap-2 p-1.5 bg-muted/50 rounded text-sm">
                              <Badge variant="outline" className={cn("text-xs", positionColors[p?.position || ''])}>
                                {p?.position}
                              </Badge>
                              <span className="flex-1 truncate font-medium">{p?.full_name}</span>
                              <button onClick={() => setTheirSelected(s => s.filter(id => id !== pid))}>
                                <X className="w-3 h-3 text-muted-foreground" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {/* Available */}
                  <div className="flex-1 overflow-auto p-2 space-y-0.5 max-h-[200px]">
                    {theirRoster.filter(r => !theirSelected.includes(r.player_id)).map(player => (
                      <button
                        key={player.player_id}
                        onClick={() => setTheirSelected(s => [...s, player.player_id])}
                        className="w-full flex items-center gap-2 p-1.5 rounded hover:bg-muted/50 transition-colors text-left text-sm"
                      >
                        <Plus className="w-3 h-3 text-muted-foreground" />
                        <Badge variant="outline" className={cn("text-xs", positionColors[player.position])}>
                          {player.position}
                        </Badge>
                        <span className="flex-1 truncate">{player.full_name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Message (optional)</label>
                <Textarea
                  placeholder="Add a message..."
                  value={tradeMessage}
                  onChange={(e) => setTradeMessage(e.target.value)}
                  rows={2}
                />
              </div>
            </>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { resetProposal(); setProposeOpen(false); }}>Cancel</Button>
            <Button
              onClick={handlePropose}
              disabled={!selectedTeamId || mySelected.length === 0 || theirSelected.length === 0 || submitting}
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send Offer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Trade Card ──────────────────────────────────────────────────────

function TradeCard({
  trade,
  perspective,
  isCommissioner,
  onAction,
}: {
  trade: TradeData;
  perspective: 'incoming' | 'outgoing' | 'review' | 'history';
  isCommissioner: boolean;
  onAction: (tradeId: string, action: string) => void;
}) {
  const statusColors: Record<string, string> = {
    pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    under_review: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    completed: 'bg-green-500/20 text-green-400 border-green-500/30',
    declined: 'bg-red-500/20 text-red-400 border-red-500/30',
    cancelled: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30',
    vetoed: 'bg-red-500/20 text-red-400 border-red-500/30',
    expired: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30',
    countered: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    failed: 'bg-red-500/20 text-red-400 border-red-500/30',
  };

  const timeLabel = trade.expires_at
    ? `Expires ${formatDistanceToNow(new Date(trade.expires_at), { addSuffix: true })}`
    : '';

  return (
    <Card>
      <CardHeader className="pb-3">
        {/* SAB-1054: wraps on a phone instead of the expiry crushing the team name. */}
        <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
          <div className="flex items-center gap-2 min-w-0">
            <CardTitle className="text-base truncate min-w-0">
              {perspective === 'incoming'
                ? trade.proposer_team_name
                : perspective === 'outgoing'
                ? trade.receiver_team_name
                : `${trade.proposer_team_name} ↔ ${trade.receiver_team_name}`}
            </CardTitle>
            <Badge variant="outline" className={cn("text-xs capitalize shrink-0", statusColors[trade.status])}>
              {trade.status.replace('_', ' ')}
            </Badge>
          </div>
          {timeLabel && trade.status === 'pending' && (
            <span className="text-xs text-muted-foreground flex items-center gap-1 shrink-0 whitespace-nowrap">
              <Clock className="w-3 h-3 shrink-0" />
              {timeLabel}
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Trade details */}
        {/* SAB-1054: `1fr` is `minmax(AUTO, 1fr)` -- the auto minimum means a column
            refuses to shrink below its content, so the truncate on the player name never
            engages and the row bleeds off the card instead. `minmax(0,1fr)` is what makes
            a grid column actually shrinkable; it is the grid analogue of min-w-0 on a flex
            child (SAB-1005, SAB-1015). */}
        <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] gap-2 sm:gap-3 items-start">
          <div className="space-y-1.5">
            <p className="text-xs text-muted-foreground truncate" title={`${trade.proposer_team_name} sends`}>
              {trade.proposer_team_name} sends
            </p>
            {(trade.proposer_player_details || []).map(p => (
              <div
                key={p.id}
                /* STACKED on narrow, inline from sm. Measured on the preview: in a
                   113.75px grid column the position badge is shrink-0 and a long label --
                   "COACH" is 82px with its padding -- leaves the name 14px even with
                   flex-1 + min-w-0 both correct. The span was doing everything right and
                   still had nothing to claim. Giving the name its own line hands it the
                   full column, the same move that fixed the propose cards. */
                className="flex flex-col items-start sm:flex-row sm:items-center gap-1 sm:gap-1.5 p-1.5 bg-muted/30 rounded text-sm min-w-0">
                <Badge variant="outline" className={cn("text-xs shrink-0", positionColors[p.position])}>
                  {p.position}
                </Badge>
                {/* flex-1 AND min-w-0, not either alone. min-w-0 only grants PERMISSION to
                    shrink; without flex-1 the span never CLAIMS the leftover space and
                    collapses toward its smallest box. Measured on the Vercel preview before
                    this line existed: "Brian Schottenheimer" rendered 14px wide at 360.
                    Removing an overflow by letting the content vanish is not a fix -- it is
                    the defect the SAB-1046 matchup avatars were gated for, inverted. */}
                <span className="w-full sm:flex-1 min-w-0 font-medium truncate" title={p.full_name}>{p.full_name}</span>
              </div>
            ))}
            {(trade.proposer_picks || []).map((pick: any, i: number) => (
              <div key={i} className="flex items-center gap-2 p-1.5 bg-muted/30 rounded text-sm">
                <Badge variant="outline" className="text-xs bg-amber-500/20 text-amber-400 border-amber-500/30">PICK</Badge>
                <span className="font-medium">{pick.year} Rd {pick.round}</span>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-center h-full pt-6">
            <ArrowRight className="w-5 h-5 text-muted-foreground" />
          </div>

          <div className="space-y-1.5">
            <p className="text-xs text-muted-foreground truncate" title={`${trade.receiver_team_name} sends`}>
              {trade.receiver_team_name} sends
            </p>
            {(trade.receiver_player_details || []).map(p => (
              <div
                key={p.id}
                /* STACKED on narrow, inline from sm. Measured on the preview: in a
                   113.75px grid column the position badge is shrink-0 and a long label --
                   "COACH" is 82px with its padding -- leaves the name 14px even with
                   flex-1 + min-w-0 both correct. The span was doing everything right and
                   still had nothing to claim. Giving the name its own line hands it the
                   full column, the same move that fixed the propose cards. */
                className="flex flex-col items-start sm:flex-row sm:items-center gap-1 sm:gap-1.5 p-1.5 bg-muted/30 rounded text-sm min-w-0">
                <Badge variant="outline" className={cn("text-xs shrink-0", positionColors[p.position])}>
                  {p.position}
                </Badge>
                {/* flex-1 AND min-w-0, not either alone. min-w-0 only grants PERMISSION to
                    shrink; without flex-1 the span never CLAIMS the leftover space and
                    collapses toward its smallest box. Measured on the Vercel preview before
                    this line existed: "Brian Schottenheimer" rendered 14px wide at 360.
                    Removing an overflow by letting the content vanish is not a fix -- it is
                    the defect the SAB-1046 matchup avatars were gated for, inverted. */}
                <span className="w-full sm:flex-1 min-w-0 font-medium truncate" title={p.full_name}>{p.full_name}</span>
              </div>
            ))}
            {(trade.receiver_picks || []).map((pick: any, i: number) => (
              <div key={i} className="flex items-center gap-2 p-1.5 bg-muted/30 rounded text-sm">
                <Badge variant="outline" className="text-xs bg-amber-500/20 text-amber-400 border-amber-500/30">PICK</Badge>
                <span className="font-medium">{pick.year} Rd {pick.round}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Message */}
        {trade.message && (
          <p className="text-sm text-muted-foreground italic bg-muted/20 p-2 rounded">"{trade.message}"</p>
        )}

        {/* Actions */}
        {perspective === 'incoming' && (
          <div className="flex gap-2 pt-1">
            <Button className="flex-1" onClick={() => onAction(trade.id, 'accept')}>
              <Check className="w-4 h-4 mr-2" />
              Accept
            </Button>
            <Button variant="destructive" onClick={() => onAction(trade.id, 'decline')}>
              <X className="w-4 h-4 mr-2" />
              Decline
            </Button>
          </div>
        )}
        {perspective === 'outgoing' && (
          <Button variant="destructive" className="w-full" onClick={() => onAction(trade.id, 'cancel')}>
            <X className="w-4 h-4 mr-2" />
            Cancel Offer
          </Button>
        )}
        {perspective === 'review' && isCommissioner && (
          <Button variant="destructive" className="w-full" onClick={() => onAction(trade.id, 'veto')}>
            <Shield className="w-4 h-4 mr-2" />
            Veto Trade
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
